/**
 * useResolveIssuesFlow — kick off a bulk "Fix with Asthra" pass, drive it on the
 * spine, and let the writer pause / stop / resume it.
 *
 * The backend reuses the existing run_id + SSE stream (see
 * ``POST /runs/{id}/resolve-issues``). Once the POST is accepted we clear the
 * terminal-run gate + bump the subscription nonce so ``useVariantCRun``
 * re-subscribes to the same run from the pass's cursor. A pass speaks only
 * ``resolve_*`` events (``resolve_started`` … ``resolve_finished``), never the
 * drafting run's ``module_start`` / ``pipeline_complete``; each section's row is
 * painted by ``resolve_section_started`` (sseAdapter).
 *
 * The pass deliberately never enters the reducer's mid-run state (so the run
 * Stop/Pause bar stays hidden — those cancel the DRAFTING run, which this is
 * not). Instead this hook owns a small phase machine fed by the resolve SSE
 * events, and its own control bar:
 *
 *   idle → running → (pausing|stopping) → paused → running (resume) → … → idle
 *
 * ``pause`` asks the server to stop at the next safe point and report the
 * remaining targets on ``resolve_finished``; ``resume`` re-POSTs exactly those.
 * ``stop`` cancels and returns to idle.
 */

import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  controlResolveIssues,
  type FixOutcome,
  getResolveIssuesStatus,
  type ResolveIssueDoc,
  resolveIssues,
} from "../../api/client";
import type { ProgressEvent } from "../../types";
import type { ResolveSelectionTarget } from "../components/SubmissionIssuesTab";

export type ResolvePhase = "idle" | "running" | "pausing" | "stopping" | "paused";

/** Minimal target shape the API takes (display fields stripped). */
export interface ResolveApiTarget {
  module_id: string;
  issue_ids: string[];
}

export interface ResolveProgress {
  /** Sections that reached their end event (proposed / failed / skipped). */
  done: number;
  /** Sections in the pass. */
  total: number;
  /** Flags (issue ids) the pass was started on. */
  flags: number;
}

/** One flag's verdict from this pass (``issue_outcomes`` on the section-end
 *  event) — the live twin of ``SectionIssue.last_fix``. */
export interface ResolveIssueOutcome {
  outcome: FixOutcome;
  message?: string | null;
  proposal_id?: string | null;
}

/** End-of-pass counts (``resolve_summary`` on ``resolve_finished``). */
export interface ResolveSummary {
  proposed: number;
  not_in_sources: number;
  /** The agent changed the text, but the change didn't fill the flag. */
  rewrite_rejected: number;
  not_placed: number;
  /** Skipped: a change for the flag was already waiting for review. */
  has_pending: number;
  /** Skipped: another pass (e.g. the automatic gap fill) was on the section. */
  busy: number;
  failed: number;
  stopped: number;
  /** Proposals queued for review. */
  proposals: number;
  pass_id?: string | null;
}

/** Read a ``resolve_summary`` payload defensively (missing counts → 0). */
export function parseResolveSummary(raw: unknown): ResolveSummary | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const n = (k: string) => (typeof r[k] === "number" && Number.isFinite(r[k]) ? (r[k] as number) : 0);
  return {
    proposed: n("proposed"),
    not_in_sources: n("not_in_sources"),
    rewrite_rejected: n("rewrite_rejected"),
    not_placed: n("not_placed"),
    has_pending: n("has_pending"),
    busy: n("busy"),
    failed: n("failed"),
    stopped: n("stopped"),
    proposals: n("proposals"),
    pass_id: typeof r.pass_id === "string" ? r.pass_id : null,
  };
}

export interface UseResolveIssuesFlowParams {
  runId: string | null;
  /** Clear the terminal-run SSE gate so useVariantCRun re-subscribes. */
  setTerminalRunStatus: (v: boolean) => void;
  /** Bump the subscription nonce so the SSE effect re-runs on the same run_id. */
  setRunSubscriptionNonce: Dispatch<SetStateAction<number>>;
  /** Set the SSE start mode — fallback when no cursor is given. */
  setSubscriptionStartMode: (mode: "history" | "latest") => void;
  /** Seed the exact stream position captured before dispatch. */
  seedStreamCursor: (id: string | null) => void;
  /** Show the pass's progress: the Issues tab, where the flags being fixed
   *  carry their live status (the Spine is for drafting). */
  onShowFixProgress: () => void;
}

export interface UseResolveIssuesFlowResult {
  busy: boolean;
  error: string | null;
  phase: ResolvePhase;
  progress: ResolveProgress;
  /** Sections left to do when paused (drives the control bar's "m left"). */
  remainingCount: number;
  /** Sections of the pass still in flight (their rows read "Fixing…"). */
  inFlightModuleIds: ReadonlySet<string>;
  /** Flags (issue ids) the pass in flight, or the last one, was started on. */
  passIssueIds: ReadonlySet<string>;
  /** Per-flag verdicts seen so far this pass, by issue id. */
  issueOutcomes: Readonly<Record<string, ResolveIssueOutcome>>;
  /** Counts from the last finished pass; null until one finishes / after dismiss. */
  summary: ResolveSummary | null;
  /** Hide the end-of-pass summary. */
  dismissSummary: () => void;
  /** Start resolving the given targets. Returns true when the POST was accepted. */
  startResolve: (targets: ResolveSelectionTarget[], documentScope: ResolveIssueDoc[]) => Promise<boolean>;
  /** Ask the pass to stop at its next safe point and remember the rest. */
  pause: () => void;
  /** Ask the pass to stop and discard the rest. */
  stop: () => void;
  /** Re-run exactly the targets left when paused. */
  resume: () => void;
  /** Drop a paused pass without resuming. */
  discard: () => void;
  /** Dismiss the last start/resume error. */
  clearError: () => void;
  /** Fold one SSE event into the phase machine — call from the shell's handler. */
  onEvent: (event: ProgressEvent) => void;
  /** The targets of a pass found still running when this client attached (a
   *  reload, a reopened pane) — so its progress can be shown again. */
  reattachedTargets: ResolveApiTarget[] | null;
}

/** Shown when a fix is asked for while the document is still drafting (the
 *  server refuses that with a 409) — shared by every entry point. */
export const RESOLVE_WHILE_DRAFTING_MESSAGE = "This document is still drafting. Fix flags once drafting finishes.";

/** Section-end events of a pass: each counts its section done once. */
const SECTION_END_EVENTS = new Set(["resolve_section_proposed", "resolve_section_failed", "resolve_section_skipped"]);

/** How long "Pausing…" / "Stopping…" may wait for the terminal event before
 *  the hook asks the server whether the pass is still running. */
const CONTROL_SETTLE_MS = 45_000;

/** The writer-facing reason a start / resume POST was refused. */
export function describeResolveError(err: unknown): string {
  const status = (err as { status?: number } | null)?.status;
  const detail = (err as { detail?: unknown } | null)?.detail;
  const code = detail && typeof detail === "object" ? (detail as { error?: unknown }).error : undefined;
  if (status === 409 && code === "resolve_active") {
    return "The review agent is already fixing flags in this document. Wait for it to finish, then try again.";
  }
  if (status === 409) {
    return RESOLVE_WHILE_DRAFTING_MESSAGE;
  }
  return `Couldn't start fixing flags: ${err instanceof Error ? err.message : String(err)}`;
}

export function useResolveIssuesFlow({
  runId,
  setTerminalRunStatus,
  setRunSubscriptionNonce,
  setSubscriptionStartMode,
  seedStreamCursor,
  onShowFixProgress,
}: UseResolveIssuesFlowParams): UseResolveIssuesFlowResult {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<ResolvePhase>("idle");
  const [progress, setProgress] = useState<ResolveProgress>({ done: 0, total: 0, flags: 0 });
  const [inFlightModuleIds, setInFlightModuleIds] = useState<ReadonlySet<string>>(() => new Set());
  const [issueOutcomes, setIssueOutcomes] = useState<Record<string, ResolveIssueOutcome>>({});
  const [passIssueIds, setPassIssueIds] = useState<ReadonlySet<string>>(() => new Set());
  const [summary, setSummary] = useState<ResolveSummary | null>(null);
  const [reattachedTargets, setReattachedTargets] = useState<ResolveApiTarget[] | null>(null);
  // Sections that already reached an end event this pass — a section is
  // counted done once, whichever end event (or repeat) arrives.
  const endedRef = useRef<Set<string>>(new Set());
  // What's left to redo after a pause — set from resolve_finished.
  const [remaining, setRemaining] = useState<{ targets: ResolveApiTarget[]; documentScope: ResolveIssueDoc[] } | null>(
    null,
  );
  // Live document scope for the pass in flight (so a mid-pass pause keeps it).
  const scopeRef = useRef<ResolveIssueDoc[]>([]);
  const remainingRef = useRef(remaining);
  remainingRef.current = remaining;

  const _post = useCallback(
    async (
      targets: ResolveApiTarget[],
      documentScope: ResolveIssueDoc[],
      // A resume carries the verdicts from before the pause, so a flag already
      // fixed does not drop back to "Fixing…" / "No change".
      carried?: { outcomes: Record<string, ResolveIssueOutcome>; issueIds: ReadonlySet<string> },
    ): Promise<boolean> => {
      if (!runId || targets.length === 0) return false;
      setBusy(true);
      setError(null);
      try {
        // POST FIRST — the endpoint calls event_bus.create_run (so the stream
        // exists before we re-subscribe; a subscribe before it would 404, which
        // EventSource never retries) and returns the stream cursor captured just
        // before the orchestrator was dispatched.
        const resp = await resolveIssues(runId, { targets, document_scope: documentScope });
        // Clear the last pass's results only once the server took the pass: a
        // refused POST (409 while the run is still drafting, or a pass already
        // running) must not wipe them. Rows are painted by the pass's own
        // resolve_section_started, so a section it never reaches stays as is.
        scopeRef.current = documentScope;
        setPhase("running");
        endedRef.current = new Set();
        setProgress({
          done: 0,
          total: targets.length,
          flags: targets.reduce((n, t) => n + t.issue_ids.length, 0),
        });
        setSummary(null);
        setIssueOutcomes(carried?.outcomes ?? {});
        setRemaining(null);
        setInFlightModuleIds(new Set(targets.map((t) => t.module_id)));
        setPassIssueIds(new Set([...(carried?.issueIds ?? []), ...targets.flatMap((t) => t.issue_ids)]));
        onShowFixProgress();
        // NOW re-subscribe FROM THE CURSOR so we replay resolve_started onward with
        // no tail race and no old-history replay.
        setSubscriptionStartMode("latest");
        seedStreamCursor(resp.stream_cursor ?? null);
        setTerminalRunStatus(false);
        setRunSubscriptionNonce((n) => n + 1);
        return true;
      } catch (err) {
        setError(describeResolveError(err));
        // A refused resume keeps its remaining targets so Resume still works.
        if (remainingRef.current) setPhase("paused");
        return false;
      } finally {
        setBusy(false);
      }
    },
    [
      runId,
      setTerminalRunStatus,
      setRunSubscriptionNonce,
      setSubscriptionStartMode,
      seedStreamCursor,
      onShowFixProgress,
    ],
  );

  const startResolve = useCallback(
    (targets: ResolveSelectionTarget[], documentScope: ResolveIssueDoc[]) =>
      // Strip display-only fields (number/title) — the API takes module_id + issue_ids.
      _post(
        targets.map((t) => ({ module_id: t.module_id, issue_ids: t.issue_ids })),
        documentScope,
      ),
    [_post],
  );

  // Ask the server whether the pass is still running; when it isn't, settle a
  // phase that is waiting on a terminal event we never saw ("Pausing…" forever).
  const settleFromServer = useCallback(async () => {
    if (!runId) return;
    try {
      const status = await getResolveIssuesStatus(runId);
      if (status.active) return;
      setInFlightModuleIds((cur) => (cur.size === 0 ? cur : new Set()));
      setPhase((cur) => {
        if (cur === "pausing" || cur === "stopping" || cur === "running") {
          return remainingRef.current ? "paused" : "idle";
        }
        return cur;
      });
    } catch {
      /* status unavailable — keep the phase; the next event or retry settles it */
    }
  }, [runId]);

  const _control = useCallback(
    (action: "pause" | "cancel", optimistic: ResolvePhase) => {
      if (!runId) return;
      setPhase(optimistic);
      void controlResolveIssues(runId, action).catch((err) => {
        // A 404 means the pass already ended between the click and the request:
        // settle from the server instead of waiting for an event we missed.
        const status = (err as { status?: number } | null)?.status;
        if (status !== 404) setError(err instanceof Error ? err.message : String(err));
        void settleFromServer();
      });
    },
    [runId, settleFromServer],
  );

  // Safety net: "Pausing…" / "Stopping…" that never hears its terminal event.
  useEffect(() => {
    if (phase !== "pausing" && phase !== "stopping") return;
    const t = setTimeout(() => void settleFromServer(), CONTROL_SETTLE_MS);
    return () => clearTimeout(t);
  }, [phase, settleFromServer]);

  // Re-attach after a reload: a pass still running on the server gets its
  // control bar back and its events replayed from the pass's stream cursor.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-check only when the run changes
  useEffect(() => {
    if (!runId) return;
    let live = true;
    getResolveIssuesStatus(runId)
      .then((status) => {
        if (!live || !status.active) return;
        endedRef.current = new Set();
        const targets = status.targets ?? [];
        setProgress({
          done: 0,
          total: status.progress?.total ?? 0,
          flags: targets.reduce((n, t) => n + t.issue_ids.length, 0),
        });
        if (targets.length > 0) {
          setInFlightModuleIds(new Set(targets.map((t) => t.module_id)));
          setPassIssueIds(new Set(targets.flatMap((t) => t.issue_ids)));
          setReattachedTargets(targets);
        }
        setPhase(status.mode === "pause" ? "pausing" : status.mode === "cancel" ? "stopping" : "running");
        setSubscriptionStartMode("latest");
        seedStreamCursor(status.stream_cursor ?? null);
        setTerminalRunStatus(false);
        setRunSubscriptionNonce((n) => n + 1);
      })
      .catch(() => {
        /* older server or no access — nothing to re-attach to */
      });
    return () => {
      live = false;
    };
  }, [runId]);

  const pause = useCallback(() => _control("pause", "pausing"), [_control]);
  const stop = useCallback(() => _control("cancel", "stopping"), [_control]);

  const resume = useCallback(() => {
    if (!remaining) return;
    // The flags being redone lose their "Stopped"; every other verdict stays.
    const redo = new Set(remaining.targets.flatMap((t) => t.issue_ids));
    const kept = Object.fromEntries(Object.entries(issueOutcomes).filter(([id]) => !redo.has(id)));
    void _post(remaining.targets, remaining.documentScope, { outcomes: kept, issueIds: passIssueIds });
  }, [_post, remaining, issueOutcomes, passIssueIds]);

  const discard = useCallback(() => {
    setPhase("idle");
    setRemaining(null);
  }, []);

  const clearError = useCallback(() => setError(null), []);
  const dismissSummary = useCallback(() => setSummary(null), []);

  const onEvent = useCallback((event: ProgressEvent) => {
    const type = event.event_type;
    if (type === "resolve_section_started") {
      setProgress((p) => ({ ...p, total: Math.max(p.total, (event.module_total ?? p.total) || p.total) }));
      return;
    }
    if (type === "resolve_flag_outcome") {
      // Verdicts as each paragraph lands — rows update before the section ends.
      const outcomes = (event.section_response as Record<string, unknown> | undefined)?.issue_outcomes;
      if (outcomes && typeof outcomes === "object") {
        setIssueOutcomes((cur) => ({ ...cur, ...(outcomes as Record<string, ResolveIssueOutcome>) }));
      }
      return;
    }
    if (SECTION_END_EVENTS.has(type)) {
      // Every section ends with exactly one of these — proposed (changes ready,
      // or nothing to rewrite, e.g. a title page with no match), failed, or
      // skipped (stopped first). Each counts once so done reaches the total.
      const resp = (event.section_response ?? {}) as Record<string, unknown>;
      const moduleId = String(event.module_id ?? resp.module_id ?? "");
      const key = moduleId || `#${endedRef.current.size}`;
      if (!endedRef.current.has(key)) {
        endedRef.current.add(key);
        const done = endedRef.current.size;
        setProgress((p) => ({ ...p, done, total: Math.max(p.total, done) }));
      }
      if (moduleId) {
        setInFlightModuleIds((cur) => {
          if (!cur.has(moduleId)) return cur;
          const next = new Set(cur);
          next.delete(moduleId);
          return next;
        });
      }
      const outcomes = resp.issue_outcomes;
      if (outcomes && typeof outcomes === "object") {
        setIssueOutcomes((cur) => ({ ...cur, ...(outcomes as Record<string, ResolveIssueOutcome>) }));
      }
      return;
    }
    if (type === "resolve_finished") {
      // The pass's one terminal event: finished | paused | stopped | failed.
      const p = (event.section_response ?? {}) as Record<string, unknown>;
      const ended = typeof p.ended === "string" ? p.ended : "finished";
      setInFlightModuleIds((cur) => (cur.size === 0 ? cur : new Set()));
      const parsed = parseResolveSummary(p.resolve_summary);
      if (parsed) setSummary(parsed);
      const rt = Array.isArray(p.remaining_targets) ? (p.remaining_targets as ResolveApiTarget[]) : [];
      if (ended === "paused" && rt.length > 0) {
        const ds = Array.isArray(p.document_scope) ? (p.document_scope as ResolveIssueDoc[]) : scopeRef.current;
        setRemaining({ targets: rt, documentScope: ds });
        setPhase("paused");
        return;
      }
      if (ended === "failed") {
        setError(event.message || "Fixing flags stopped because of an error. Try again.");
      }
      setRemaining(null);
      setPhase("idle");
      return;
    }
  }, []);

  return {
    busy,
    error,
    phase,
    progress,
    remainingCount: remaining?.targets.length ?? 0,
    inFlightModuleIds,
    passIssueIds,
    issueOutcomes,
    summary,
    dismissSummary,
    startResolve,
    pause,
    stop,
    resume,
    discard,
    clearError,
    onEvent,
    reattachedTargets,
  };
}
